/**
 * Date-only helpers on "YYYY-MM-DD" strings (G-4), using UTC arithmetic so the host time zone never matters.
 * Month strings are "YYYY-MM".
 */

export type IsoDate = string;
export type YearMonth = string;

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

function parts(iso: IsoDate): [number, number, number] {
  const m = ISO_DATE.exec(iso);
  if (!m) throw new Error(`Invalid ISO date: ${iso}`);
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

function pad(n: number, w = 2): string {
  return String(n).padStart(w, '0');
}

export function isValidIsoDate(s: string): boolean {
  const m = ISO_DATE.exec(s);
  if (!m) return false;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1) return false;
  return d <= daysInMonthOf(y, mo);
}

export function toUtcMs(iso: IsoDate): number {
  const [y, m, d] = parts(iso);
  return Date.UTC(y, m - 1, d);
}

export function fromUtcMs(ms: number): IsoDate {
  const dt = new Date(ms);
  return `${pad(dt.getUTCFullYear(), 4)}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

/** Date column value (UTC midnight) → "YYYY-MM-DD". */
export function dateToIso(d: Date): IsoDate {
  return d.toISOString().slice(0, 10);
}

/** "YYYY-MM-DD" → Date at UTC midnight (what Prisma expects for @db.Date). */
export function isoToDate(iso: IsoDate): Date {
  return new Date(toUtcMs(iso));
}

export function addDays(iso: IsoDate, n: number): IsoDate {
  return fromUtcMs(toUtcMs(iso) + n * DAY_MS);
}

/** a − b in whole days. */
export function diffDays(a: IsoDate, b: IsoDate): number {
  return Math.round((toUtcMs(a) - toUtcMs(b)) / DAY_MS);
}

function daysInMonthOf(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** Calendar months; the day is clamped to the end of the target month (31 Jan + 1 month = 28/29 Feb). */
export function addMonths(iso: IsoDate, n: number): IsoDate {
  const [y, m, d] = parts(iso);
  const total = y * 12 + (m - 1) + n;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${pad(ny, 4)}-${pad(nm)}-${pad(Math.min(d, daysInMonthOf(ny, nm)))}`;
}

export function compareDates(a: IsoDate, b: IsoDate): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export function maxDate(...dates: (IsoDate | null | undefined)[]): IsoDate | null {
  let best: IsoDate | null = null;
  for (const d of dates) if (d && (best === null || d > best)) best = d;
  return best;
}

export function minDate(...dates: (IsoDate | null | undefined)[]): IsoDate | null {
  let best: IsoDate | null = null;
  for (const d of dates) if (d && (best === null || d < best)) best = d;
  return best;
}

/** Every date from `from` to `to`, inclusive. */
export function eachDay(from: IsoDate, to: IsoDate): IsoDate[] {
  const out: IsoDate[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

export function monthOf(iso: IsoDate): YearMonth {
  return iso.slice(0, 7);
}

export function firstOfMonth(month: YearMonth): IsoDate {
  return `${month}-01`;
}

export function lastOfMonth(month: YearMonth): IsoDate {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return `${month}-${pad(daysInMonthOf(y, m))}`;
}

export function daysInMonth(month: YearMonth): number {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return daysInMonthOf(y, m);
}

export function addMonthsToMonth(month: YearMonth, n: number): YearMonth {
  return addMonths(firstOfMonth(month), n).slice(0, 7);
}

export function isValidYearMonth(s: string): boolean {
  return /^\d{4}-(0[1-9]|1[0-2])$/.test(s);
}

interface LocalParts {
  date: IsoDate;
  minutes: number;
}

function localParts(timeZone: string, now: Date): LocalParts {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const p: Record<string, string> = {};
  for (const part of fmt.formatToParts(now)) p[part.type] = part.value;
  const hour = Number(p.hour) % 24;
  return { date: `${p.year}-${p.month}-${p.day}`, minutes: hour * 60 + Number(p.minute) };
}

/** "Today" in the farm's time zone (G-4), never UTC. */
export function todayInTimeZone(timeZone: string, now: Date = new Date()): IsoDate {
  return localParts(timeZone, now).date;
}

/** Local wall-clock time in the farm's time zone as "YYYY-MM-DDTHH:mm". */
export function localDateTimeInTimeZone(timeZone: string, now: Date = new Date()): string {
  const p = localParts(timeZone, now);
  return `${p.date}T${pad(Math.floor(p.minutes / 60))}:${pad(p.minutes % 60)}`;
}

/** "06:30" → 390 */
export function hhmmToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return h * 60 + m;
}

/** Local date + minutes after midnight (may exceed 1440) → "YYYY-MM-DDTHH:mm". */
export function localDateTime(date: IsoDate, minutes: number): string {
  const dayOffset = Math.floor(minutes / 1440);
  const rest = minutes - dayOffset * 1440;
  return `${addDays(date, dayOffset)}T${pad(Math.floor(rest / 60))}:${pad(rest % 60)}`;
}

/** Whole years between two dates (for "not more than 25 years ago"). */
export function yearsBetween(from: IsoDate, to: IsoDate): number {
  const [fy, fm, fd] = parts(from);
  const [ty, tm, td] = parts(to);
  let years = ty - fy;
  if (tm < fm || (tm === fm && td < fd)) years -= 1;
  return years;
}

/** Whole calendar months between two dates. */
export function monthsBetween(from: IsoDate, to: IsoDate): number {
  const [fy, fm, fd] = parts(from);
  const [ty, tm, td] = parts(to);
  let months = (ty - fy) * 12 + (tm - fm);
  if (td < fd && addMonths(from, months) > to) months -= 1;
  return months;
}
